// Player search box — type a name, pick from live results (/player-search).
// Matches Catalyst's search-first player selection. On pick, the parent seeds
// the player's most-recent competition/season.

import { useEffect, useRef, useState } from "react";
import type { PlayerSearchResult } from "@catbento/shared";
import { usePlayerSearch } from "../data/players.js";
import { theme } from "../lib/theme.js";

export default function PlayerSearchSelect({
  currentName,
  onSelect,
}: {
  currentName: string | null;
  onSelect: (p: PlayerSearchResult) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);
  const search = usePlayerSearch(query);
  const results = search.data?.players ?? [];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={boxRef} style={{ position: "relative", minWidth: 260 }}>
      <input
        value={open ? query : currentName ?? ""}
        placeholder={currentName ? currentName : "Search a player…"}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: theme.input,
          color: theme.ink,
          border: `1px solid ${theme.borderInput}`,
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 14,
        }}
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            background: theme.tile,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            maxHeight: 300,
            overflowY: "auto",
          }}
        >
          {query.trim().length < 3 ? (
            <Row muted>Type at least 3 letters…</Row>
          ) : search.isPending ? (
            <Row muted>Searching…</Row>
          ) : results.length === 0 ? (
            <Row muted>No players found.</Row>
          ) : (
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onSelect(p);
                  setOpen(false);
                  setQuery("");
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  color: theme.ink,
                  border: "none",
                  padding: "8px 12px",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {p.name}
                {p.team ? <span style={{ color: theme.inkFaint }}> · {p.team}</span> : null}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Row({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <div style={{ padding: "9px 12px", color: muted ? theme.inkFaint : theme.ink, fontSize: 13 }}>
      {children}
    </div>
  );
}
